import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, MagnifyingGlass, X } from "@phosphor-icons/react";
import { lookupPostcode } from "@/lib/postcodes";
import { HALLS, slug } from "@/lib/areas";

export default function PostcodeLookup() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [searched, setSearched] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    const r = lookupPostcode(code);
    setResult(r);
    setSearched(true);
  };

  const reset = () => {
    setCode("");
    setResult(null);
    setSearched(false);
  };

  return (
    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 lg:p-7" data-testid="postcode-lookup">
      <div className="flex items-center gap-2 mb-3 text-[#E2B054]">
        <MapPin size={20} weight="duotone" />
        <p className="text-sm uppercase tracking-wider font-semibold">Where are you based?</p>
      </div>
      <p className="text-white/80 text-sm mb-4">Pop in your postcode and we'll show you the nearest puppy school hall plus services available in your area.</p>

      <form onSubmit={submit} className="flex gap-2">
        <input
          data-testid="postcode-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]{4}"
          maxLength={4}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
          placeholder="e.g. 4575"
          className="flex-1 rounded-lg px-4 py-3 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-[#f38838]/50 font-semibold"
        />
        <button
          data-testid="postcode-submit"
          type="submit"
          disabled={code.length !== 4}
          className="btn-primary disabled:opacity-50 inline-flex items-center gap-2"
        >
          <MagnifyingGlass size={18} weight="bold" /> Check
        </button>
      </form>

      {searched && result && (
        <div className="mt-5 bg-white rounded-xl p-5 text-stone-800 fade-up" data-testid="postcode-result">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-[#f38838] mb-1">{result.region}</div>
              <div className="font-heading font-semibold text-lg text-stone-900">Yes — we service postcode {code}!</div>
            </div>
            <button onClick={reset} className="text-stone-400 hover:text-stone-700" aria-label="Clear">
              <X size={18} />
            </button>
          </div>

          <div className="bg-[#FDFBF7] border border-stone-200 rounded-lg p-3 mb-3">
            <div className="text-xs uppercase tracking-wider font-semibold text-[#1b4a76] mb-1">Closest Puppy School hall</div>
            <div className="font-semibold text-stone-900">{HALLS[result.hall].name}</div>
            <div className="text-xs text-stone-600">{HALLS[result.hall].address}</div>
            <div className="text-xs font-semibold text-[#f38838] mt-1">{HALLS[result.hall].times}</div>
          </div>

          {result.suburbs && result.suburbs.length > 0 && (
            <div className="mb-3">
              <div className="text-xs text-stone-500 mb-1.5 font-semibold uppercase tracking-wider">Suburbs in {code}</div>
              <div className="flex flex-wrap gap-1.5">
                {result.suburbs.slice(0, 4).map((s) => (
                  <Link
                    key={s}
                    to={`/service-areas/${slug(s)}`}
                    className="text-xs bg-stone-100 hover:bg-[#f38838] hover:text-white px-3 py-1.5 rounded-full font-medium transition-colors"
                    data-testid={`postcode-suburb-${slug(s)}`}
                  >
                    {s}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <Link to="/register" className="btn-primary text-sm w-full justify-center inline-flex items-center gap-2 mt-2" data-testid="postcode-book-cta">
            Book Now <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      )}

      {searched && !result && (
        <div className="mt-5 bg-white rounded-xl p-5 text-stone-800 fade-up" data-testid="postcode-no-result">
          <div className="font-heading font-semibold text-stone-900 mb-1">Postcode {code} isn't in our common service list.</div>
          <p className="text-sm text-stone-600 mb-3">We may still be able to help — the team travels across the Sunshine Coast for private services.</p>
          <a href="mailto:support@puppl.app" className="btn-primary text-sm inline-flex items-center gap-2">
            Email the team to confirm <ArrowRight size={16} weight="bold" />
          </a>
        </div>
      )}
    </div>
  );
}
